import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteAdminUser,
  getAdminUsers,
  updateAdminUser,
  type AdminPageMeta,
  type AdminUserItem,
} from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SortableHead } from "@/components/admin/SortableHead";
import {
  BadgeCheckIcon,
  Pencil,
  Search,
  ShieldCheckIcon,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/users")({
  component: AdminUsers,
});

type SortKey = "createdAt" | "name" | "email" | "pagesCount" | "viewsCount";

// sensible starting direction the first time a column is clicked
const DEFAULT_ORDER: Record<SortKey, "asc" | "desc"> = {
  name: "asc",
  email: "asc",
  createdAt: "desc",
  pagesCount: "desc",
  viewsCount: "desc",
};

function AdminUsers() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [editTarget, setEditTarget] = useState<AdminUserItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserItem | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-users", { page, pageSize, search, sortBy, sortOrder }],
    queryFn: async () => {
      const res = await getAdminUsers({ page, pageSize, search, sortBy, sortOrder });
      if (!res.ok) throw new Error("failed to load users");
      return (await res.json()) as { items: AdminUserItem[]; page: AdminPageMeta };
    },
    // keep the current rows visible while the next sort/page/search loads
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteAdminUser(id);
      if (!res.ok) throw new Error((await res.json())?.error || "failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("user deleted");
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submitSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleSort = (column: string) => {
    const col = column as SortKey;
    if (sortBy === col) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortOrder(DEFAULT_ORDER[col] ?? "desc");
    }
    setPage(1);
  };

  const meta = data?.page;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">users</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search className="h-4 w-4 text-gray-400" />
            </span>
            <Input
              placeholder="search name, username, email"
              className="w-64 rounded-2xl bg-white/70 pl-9"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            />
          </div>
        </div>
      </div>

      <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
        <CardContent
          className={`p-4 transition-opacity duration-200 ${
            isFetching && !isLoading ? "opacity-60" : ""
          }`}
          aria-busy={isFetching}
        >
          {isLoading ? (
            <div className="space-y-2 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-10 bg-amber-100/40 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center text-gray-500">no users found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-amber-200/50">
                  <SortableHead
                    label="user"
                    column="name"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHead
                    label="email"
                    column="email"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                  />
                  <TableHead>flags</TableHead>
                  <SortableHead
                    label="pages"
                    column="pagesCount"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHead
                    label="views"
                    column="viewsCount"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHead
                    label="joined"
                    column="createdAt"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                  />
                  <TableHead className="text-right">actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((u) => (
                  <TableRow key={u.id} className="border-amber-100/60">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 border border-amber-200">
                          <AvatarImage src={u.image || undefined} />
                          <AvatarFallback className="bg-amber-100 text-amber-800 text-xs">
                            {(u.name || u.email).charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-gray-900">{u.name || "—"}</div>
                          <div className="text-xs text-gray-500">
                            {u.displayUsername || u.username || "no username"}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-gray-700">{u.email}</div>
                      {!u.emailVerified && (
                        <Badge
                          variant="outline"
                          className="mt-0.5 border-red-200 text-red-600 text-[10px]"
                        >
                          unverified
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {u.verified && (
                          <span title="verified">
                            <BadgeCheckIcon className="h-4 w-4 text-accent" />
                          </span>
                        )}
                        {u.isSiteAdmin && (
                          <span title="site admin">
                            <ShieldCheckIcon className="h-4 w-4 text-amber-600" />
                          </span>
                        )}
                        {!u.verified && !u.isSiteAdmin && (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{u.pagesCount}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {u.viewsCount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl hover:bg-amber-100"
                          onClick={() => setEditTarget(u)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl hover:bg-red-100 text-red-600"
                          onClick={() => setDeleteTarget(u)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {meta && meta.total > 0 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                showing {(meta.page - 1) * meta.pageSize + 1}-
                {Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  previous
                </Button>
                <div className="text-sm text-gray-700">
                  page {meta.page} of {meta.totalPages}
                </div>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EditUserDialog target={editTarget} onClose={() => setEditTarget(null)} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>delete user</DialogTitle>
            <DialogDescription>
              delete {deleteTarget?.email}? this removes their account, pages, and
              sessions permanently. this cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleteMutation.isPending || !deleteTarget}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? "deleting..." : "delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUserDialog({
  target,
  onClose,
}: {
  target: AdminUserItem | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<{
    name: string;
    email: string;
    username: string;
    emailVerified: boolean;
    verified: boolean;
    isSiteAdmin: boolean;
  } | null>(null);

  // seed form when a new target is opened
  const [seedId, setSeedId] = useState<string | null>(null);
  if (target && target.id !== seedId) {
    setSeedId(target.id);
    setForm({
      name: target.name || "",
      email: target.email || "",
      username: target.displayUsername || target.username || "",
      emailVerified: !!target.emailVerified,
      verified: !!target.verified,
      isSiteAdmin: !!target.isSiteAdmin,
    });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!target || !form) return;
      const payload: Record<string, unknown> = {
        emailVerified: form.emailVerified,
        verified: form.verified,
        isSiteAdmin: form.isSiteAdmin,
      };
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.username.trim()) payload.username = form.username.trim();
      const res = await updateAdminUser(target.id, payload);
      if (!res.ok) throw new Error((await res.json())?.error || "failed to save");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("user updated");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const close = () => {
    setSeedId(null);
    setForm(null);
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>edit user</DialogTitle>
          <DialogDescription>
            editing {target?.email} — changes apply immediately
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="admin-edit-name">name</Label>
              <Input
                id="admin-edit-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-edit-email">email</Label>
              <Input
                id="admin-edit-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-edit-username">username</Label>
              <Input
                id="admin-edit-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div className="space-y-3 pt-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="admin-edit-emailverified">email verified</Label>
                <Switch
                  id="admin-edit-emailverified"
                  checked={form.emailVerified}
                  onCheckedChange={(v) => setForm({ ...form, emailVerified: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="admin-edit-verified">verified badge</Label>
                <Switch
                  id="admin-edit-verified"
                  checked={form.verified}
                  onCheckedChange={(v) => setForm({ ...form, verified: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="admin-edit-admin">site admin</Label>
                <Switch
                  id="admin-edit-admin"
                  checked={form.isSiteAdmin}
                  onCheckedChange={(v) => setForm({ ...form, isSiteAdmin: v })}
                />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saveMutation.isPending}>
            cancel
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-white"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "saving..." : "save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
